import React, { useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useContent } from "@/context/ContentContext";
import { useLang } from "@/i18n/LanguageContext";
import { Hero } from "@/components/Hero";
import { LatestUpdates } from "@/components/LatestUpdates";
import { SectionGrid } from "@/components/SectionGrid";
import { JourneyProgress } from "@/components/JourneyProgress";
import { TodayJourney } from "@/components/TodayJourney";
import { PrayerTimes } from "@/components/PrayerTimes";
import { SpiritualTools } from "@/components/SpiritualTools";
import { InstallPrompt } from "@/components/InstallPrompt";
import { BottomNav } from "@/components/BottomNav";
import { Loader } from "@/components/Loader";

export default function JourneyHome() {
  const { content, loading } = useContent();
  const { L } = useLang();
  const homeRef = useRef(null);
  const updatesRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.scrollTo === "updates") {
      setTimeout(() => updatesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [location.state]);

  // Persist the Home scroll position continuously
  useEffect(() => {
    const onScroll = () => sessionStorage.setItem("maqam_home_scroll", String(window.scrollY));
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Restore scroll on return from a section (unless the Home tab asked for top)
  useEffect(() => {
    if (loading || !content) return;
    if (sessionStorage.getItem("maqam_home_top") === "1") {
      sessionStorage.removeItem("maqam_home_top");
      window.scrollTo(0, 0);
      return;
    }
    if (location.state?.scrollTo) return;
    const saved = parseInt(sessionStorage.getItem("maqam_home_scroll") || "0", 10);
    if (saved > 0) {
      const restore = () => window.scrollTo(0, saved);
      requestAnimationFrame(restore);
      setTimeout(restore, 140);
      setTimeout(restore, 360);
    }
  }, [loading, content]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !content) return <Loader />;

  const settings = content.settings || {};
  const status = settings.status || "active";
  const isAdmin = !content.restricted && status !== "active";

  const scrollToHome = () => homeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="min-h-screen bg-[#F7F4EE]" data-testid="journey-home">
      {isAdmin && status !== "active" && (
        <div className="fixed top-0 z-[60] w-full bg-[#D4AF37] px-4 py-1.5 text-center text-xs font-bold text-[#0A192F]">
          Admin preview — this journey is {status.toUpperCase()} and hidden from travelers
        </div>
      )}
      <Hero settings={settings} onBegin={scrollToHome} />

      <main ref={homeRef} className="mx-auto w-full max-w-xl scroll-mt-2 space-y-10 px-5 pb-28 pt-9 sm:px-6">
        <section className="animate-fade-up" data-testid="welcome-message">
          <div className="maqam-card relative overflow-hidden p-7">
            <span className="absolute left-0 top-6 h-12 w-1 rounded-r-full bg-[#D4AF37]" />
            <p className="font-arabic text-lg text-[#BCA054]">{settings.greeting}</p>
            <p className="mt-3 text-[15px] leading-[1.75] text-slate-600">{L(settings.welcomeBody)}</p>
          </div>
        </section>
        <JourneyProgress settings={settings} />
        <TodayJourney settings={settings} itinerary={content.itinerary || []} />
        <PrayerTimes settings={settings} />
        <SpiritualTools />
        <LatestUpdates announcements={content.announcements || []} innerRef={updatesRef} />
        <SectionGrid />
        <footer className="pt-2 text-center">
          <p className="font-arabic text-base text-slate-400">بارك الله فيكم</p>
          <p className="mt-1 text-xs text-slate-400">May Allah accept your Umrah</p>
        </footer>
      </main>

      <InstallPrompt />
      <BottomNav />
    </div>
  );
}
